import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ImagePlus, X, DollarSign, Car, Users, Calendar, Upload, Images, GripVertical } from "lucide-react";

const emptyVehicle = {
  name: "", category: "Sedan", daily_price: 79, passengers: 5,
  transmission: "Automatic", fuel_type: "Petrol", air_conditioning: true, available: true,
  image_url: "", gallery: [], description: "",
};

const backendOrigin = API.replace(/\/api$/, "");
const resolveUrl = (u) => (u?.startsWith("http") ? u : `${backendOrigin}${u}`);

export default function Admin() {
  const { user, loading } = useAuth() || {};
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  const [stats, setStats] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [editing, setEditing] = useState(null);
  const [galleryFor, setGalleryFor] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (user.role !== "admin") { nav("/"); return; }
    refresh();
  }, [user, loading]);

  const onTabChange = (t) => {
    setTab(t);
    setSearchParams(t === "overview" ? {} : { tab: t });
  };

  const refresh = async () => {
    const [s, v, b, c, g] = await Promise.all([
      api.get("/admin/stats"), api.get("/vehicles"),
      api.get("/admin/bookings"), api.get("/admin/customers"),
      api.get("/gallery"),
    ]);
    setStats(s.data); setVehicles(v.data); setBookings(b.data); setCustomers(c.data); setGallery(g.data);
  };

  const save = async (v) => {
    try {
      if (v.id) await api.put(`/vehicles/${v.id}`, stripFields(v));
      else await api.post("/vehicles", stripFields(v));
      toast.success("Vehicle saved");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    await api.delete(`/vehicles/${id}`);
    toast.success("Deleted");
    refresh();
  };

  if (!stats) return <div className="p-20">Loading admin…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2" style={{ color: "#0B1F3A" }}>Admin</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tighter">Command centre</h1>
        </div>
      </div>

      <Tabs value={tab} onValueChange={onTabChange} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="tab-vehicles">Vehicles</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">Customers</TabsTrigger>
          <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={DollarSign} label="Revenue" value={`$${stats.revenue.toFixed(2)}`} />
            <StatCard icon={Calendar} label="Bookings" value={`${stats.paid_bookings} / ${stats.total_bookings}`} />
            <StatCard icon={Users} label="Customers" value={stats.total_customers} />
            <StatCard icon={Car} label="Vehicles" value={stats.total_vehicles} />
          </div>
          <div className="bg-white rounded-2xl border border-black/5 p-6">
            <div className="font-display font-bold text-xl mb-4">Recent bookings</div>
            <table className="w-full text-sm">
              <thead className="text-left text-[#0B1F3A]/60 text-xs uppercase tracking-widest">
                <tr><th className="py-2">Ref</th><th>Vehicle</th><th>Customer</th><th>Status</th><th className="text-right">Total</th></tr>
              </thead>
              <tbody>
                {stats.recent_bookings.map((b) => (
                  <tr key={b.id} className="border-t border-black/5">
                    <td className="py-2 font-mono-alt text-xs">{b.reference}</td>
                    <td>{b.vehicle_name}</td><td>{b.customer_name}</td>
                    <td><span className="text-xs px-2 py-1 rounded-full bg-[#0B1F3A]/5">{b.status.replace("_", " ")}</span></td>
                    <td className="text-right font-medium">${b.total_amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="vehicles">
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-[#0B1F3A]/60">{vehicles.length} vehicles</div>
            <Dialog>
              <DialogTrigger asChild>
                <button className="btn-gold !py-2 !px-4 !text-sm" data-testid="admin-add-vehicle" onClick={() => setEditing({ ...emptyVehicle })}>
                  <Plus className="w-4 h-4" /> Add vehicle
                </button>
              </DialogTrigger>
              {editing && <VehicleFormDialog v={editing} setV={setEditing} onSave={save} />}
            </Dialog>
          </div>
          <div className="grid gap-3">
            {vehicles.map((v) => (
              <div key={v.id} className="bg-white border border-black/5 rounded-xl p-4 flex items-center gap-4" data-testid={`admin-vehicle-${v.id}`}>
                <img src={resolveUrl(v.image_url)} alt="" className="w-24 h-16 object-cover rounded-lg" />
                <div className="flex-1">
                  <div className="font-display font-bold">{v.name}</div>
                  <div className="text-xs text-[#0B1F3A]/60">{v.category} · ${v.daily_price}/day · {v.passengers} seats · {v.available ? "Available" : "Unavailable"}</div>
                </div>
                <Dialog>
                  <DialogTrigger asChild>
                    <button className="p-2 rounded-lg hover:bg-black/5" onClick={() => setGalleryFor(v)} data-testid={`admin-vehicle-gallery-${v.id}`}><ImagePlus className="w-4 h-4" /></button>
                  </DialogTrigger>
                  {galleryFor?.id === v.id && <VehicleGalleryDialog vehicle={galleryFor} setVehicle={setGalleryFor} onChanged={refresh} />}
                </Dialog>
                <Dialog>
                  <DialogTrigger asChild>
                    <button className="p-2 rounded-lg hover:bg-black/5" onClick={() => setEditing({ ...v })} data-testid={`admin-vehicle-edit-${v.id}`}><Pencil className="w-4 h-4" /></button>
                  </DialogTrigger>
                  {editing?.id === v.id && <VehicleFormDialog v={editing} setV={setEditing} onSave={save} />}
                </Dialog>
                <button className="p-2 rounded-lg hover:bg-red-50 text-red-600" onClick={() => del(v.id)} data-testid={`admin-vehicle-delete-${v.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="bookings">
          <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-left text-[#0B1F3A]/60 text-xs uppercase tracking-widest bg-[#F5F5F5]">
                <tr><th className="p-3">Ref</th><th>Vehicle</th><th>Customer</th><th>Dates</th><th>Status</th><th className="text-right p-3">Total</th></tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-t border-black/5" data-testid={`admin-booking-${b.reference}`}>
                    <td className="p-3 font-mono-alt text-xs">{b.reference}</td>
                    <td>{b.vehicle_name}</td>
                    <td>{b.customer_name}<br /><span className="text-xs text-[#0B1F3A]/50">{b.customer_email}</span></td>
                    <td className="text-xs">{new Date(b.pickup_datetime).toLocaleDateString()} → {new Date(b.return_datetime).toLocaleDateString()}</td>
                    <td><span className="text-xs px-2 py-1 rounded-full bg-[#0B1F3A]/5">{b.status.replace("_", " ")}</span></td>
                    <td className="text-right p-3 font-medium">${b.total_amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="customers">
          <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-left text-[#0B1F3A]/60 text-xs uppercase tracking-widest bg-[#F5F5F5]">
                <tr><th className="p-3">Name</th><th>Email</th><th>Joined</th></tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id} className="border-t border-black/5" data-testid={`admin-customer-${c.id}`}>
                    <td className="p-3">{c.name}</td><td>{c.email}</td>
                    <td className="text-xs text-[#0B1F3A]/60">{new Date(c.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="gallery">
          <GalleryTab items={gallery} onChanged={refresh} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function stripFields(v) {
  const { id, created_at, gallery, ...rest } = v;
  return rest;
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6" data-testid={`stat-${label.toLowerCase()}`}>
      <Icon className="w-6 h-6 text-[#D4AF37] mb-3" />
      <div className="text-xs uppercase tracking-widest text-[#0B1F3A]/50">{label}</div>
      <div className="font-display font-extrabold text-3xl">{value}</div>
    </div>
  );
}

function GalleryTab({ items, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState("");
  const [dragOverUpload, setDragOverUpload] = useState(false);
  const [order, setOrder] = useState(items);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const fileRef = useRef();

  useEffect(() => { setOrder(items); }, [items]);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    const imageFiles = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (imageFiles.length === 0) { toast.error("Only image files are allowed"); return; }
    setBusy(true);
    try {
      for (const f of imageFiles) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("caption", caption);
        await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Uploaded ${imageFiles.length} photo${imageFiles.length > 1 ? "s" : ""}`);
      setCaption("");
      if (fileRef.current) fileRef.current.value = "";
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the site gallery?")) return;
    await api.delete(`/gallery/${id}`);
    toast.success("Photo removed");
    onChanged();
  };

  const onDropZone = (e) => {
    e.preventDefault();
    setDragOverUpload(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) upload(e.dataTransfer.files);
  };

  const persistOrder = async (newList) => {
    try {
      await api.post("/gallery/reorder", { ordered_ids: newList.map((g) => g.id) });
      toast.success("Order saved");
    } catch (e) {
      toast.error("Failed to save order");
      onChanged();
    }
  };

  const onItemDragStart = (i) => setDragIndex(i);
  const onItemDragOver = (e, i) => { e.preventDefault(); if (i !== dragOverIndex) setDragOverIndex(i); };
  const onItemDrop = (e, i) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) { setDragIndex(null); setDragOverIndex(null); return; }
    const next = [...order];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(i, 0, moved);
    setOrder(next);
    setDragIndex(null);
    setDragOverIndex(null);
    persistOrder(next);
  };

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOverUpload(true); }}
        onDragLeave={() => setDragOverUpload(false)}
        onDrop={onDropZone}
        className={`bg-white rounded-2xl border-2 border-dashed p-8 mb-6 transition-colors ${dragOverUpload ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-black/10"}`}
        data-testid="gallery-drop-zone"
      >
        <div className="font-display font-bold text-xl mb-1 flex items-center gap-2"><Images className="w-5 h-5 text-[#D4AF37]" /> Drop photos here to upload</div>
        <p className="text-sm text-[#0B1F3A]/60 mb-4">Drag JPG, PNG or WebP files onto this box, or click to browse. Max 10 MB each. Photos appear instantly on the public /gallery page.</p>
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <label className="md:col-span-2 block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#0B1F3A]/60">Caption (optional, applied to next upload)</span>
            <input value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="gallery-caption"
              placeholder="e.g. Morning drive through downtown Plano"
              className="mt-2 w-full border border-black/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#D4AF37]" />
          </label>
          <label className={`btn-gold justify-center cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid="gallery-upload-btn">
            <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Choose photos"}
            <input ref={fileRef} type="file" accept="image/*" multiple hidden
              onChange={(e) => upload(e.target.files)} data-testid="gallery-file-input" />
          </label>
        </div>
      </div>

      <div className="mb-4 text-sm text-[#0B1F3A]/60 flex items-center justify-between">
        <span>{order.length} uploaded photo{order.length !== 1 ? "s" : ""}</span>
        {order.length > 1 && <span className="text-xs flex items-center gap-1 text-[#0B1F3A]/50"><GripVertical className="w-3.5 h-3.5" /> Drag photos to reorder</span>}
      </div>
      {order.length === 0 ? (
        <div className="bg-white border border-dashed border-black/10 rounded-2xl p-12 text-center text-[#0B1F3A]/50">
          No photos yet. Upload your first one to get started.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {order.map((g, i) => (
            <div key={g.id}
              draggable
              onDragStart={() => onItemDragStart(i)}
              onDragOver={(e) => onItemDragOver(e, i)}
              onDrop={(e) => onItemDrop(e, i)}
              onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
              className={`relative rounded-xl overflow-hidden border bg-white group cursor-move transition-[transform,box-shadow,border-color] ${dragOverIndex === i && dragIndex !== i ? "border-[#D4AF37] scale-[1.02] shadow-lg" : "border-black/5"} ${dragIndex === i ? "opacity-50" : ""}`}
              data-testid={`gallery-admin-${g.id}`}
            >
              <div className="absolute top-2 left-2 bg-black/60 text-white rounded-md px-2 py-0.5 text-[10px] font-mono-alt flex items-center gap-1 z-10">
                <GripVertical className="w-3 h-3" /> {i + 1}
              </div>
              <img src={resolveUrl(g.url)} alt={g.caption || ""} draggable={false} className="w-full aspect-square object-cover pointer-events-none" />
              {g.caption && <div className="p-3 text-sm text-[#0B1F3A]/80">{g.caption}</div>}
              <button onClick={() => remove(g.id)} data-testid={`gallery-remove-${g.id}`}
                className="absolute top-2 right-2 bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VehicleGalleryDialog({ vehicle, setVehicle, onChanged }) {
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const addUrl = async () => {
    if (!url) return;
    await api.post(`/vehicles/${vehicle.id}/gallery`, { url });
    setUrl("");
    const { data } = await api.get(`/vehicles/${vehicle.id}`);
    setVehicle(data);
    onChanged();
  };

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post(`/vehicles/${vehicle.id}/gallery`, { url: data.url });
      }
      toast.success(`Added ${files.length} photo${files.length > 1 ? "s" : ""}`);
      const { data } = await api.get(`/vehicles/${vehicle.id}`);
      setVehicle(data);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImage = async (u) => {
    await api.delete(`/vehicles/${vehicle.id}/gallery`, { params: { url: u } });
    const { data } = await api.get(`/vehicles/${vehicle.id}`);
    setVehicle(data);
    onChanged();
  };

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader><DialogTitle>Gallery — {vehicle.name}</DialogTitle></DialogHeader>
      <div className="grid grid-cols-3 gap-3 max-h-72 overflow-y-auto">
        {(vehicle.gallery || []).map((u, i) => (
          <div key={i} className="relative">
            <img src={resolveUrl(u)} alt="" className="w-full aspect-square object-cover rounded-lg" />
            <button onClick={() => removeImage(u)} className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1" data-testid={`vgallery-remove-${i}`}>
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {(vehicle.gallery || []).length === 0 && (
          <div className="col-span-3 text-center text-sm text-[#0B1F3A]/50 py-6 border border-dashed border-black/10 rounded-lg">No photos yet.</div>
        )}
      </div>
      <div className="border-t border-black/5 pt-4 space-y-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#0B1F3A]/60">Upload photos</span>
          <div className="mt-2">
            <label className={`btn-gold justify-center cursor-pointer w-full ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
              <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Choose photos"}
              <input ref={fileRef} type="file" accept="image/*" multiple hidden
                onChange={(e) => upload(e.target.files)} data-testid="vgallery-upload" />
            </label>
          </div>
        </label>
        <div className="text-center text-xs text-[#0B1F3A]/40">— or paste an image URL —</div>
        <div className="flex gap-2">
          <input placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)}
            data-testid="vgallery-url"
            className="flex-1 border border-black/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" />
          <button onClick={addUrl} className="btn-ghost !py-2 !px-4 !text-sm !text-[#0B1F3A] !border-black/10" data-testid="vgallery-url-add">Add URL</button>
        </div>
      </div>
    </DialogContent>
  );
}

function VehicleFormDialog({ v, setV, onSave }) {
  const update = (k, val) => setV({ ...v, [k]: val });
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const uploadMain = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", files[0]);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("image_url", data.url);
      toast.success("Main photo uploaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>{v.id ? "Edit" : "Add"} vehicle</DialogTitle></DialogHeader>
      <div className="grid sm:grid-cols-2 gap-4">
        <FormField label="Name" value={v.name} onChange={(x) => update("name", x)} testId="admin-form-name" />
        <FormSelect label="Category" value={v.category} onChange={(x) => update("category", x)} options={["Economy", "Sedan", "Luxury", "SUV"]} testId="admin-form-category" />
        <FormField label="Daily price ($)" type="number" value={v.daily_price} onChange={(x) => update("daily_price", parseFloat(x) || 0)} testId="admin-form-price" />
        <FormField label="Passengers" type="number" value={v.passengers} onChange={(x) => update("passengers", parseInt(x) || 1)} testId="admin-form-passengers" />
        <FormSelect label="Transmission" value={v.transmission} onChange={(x) => update("transmission", x)} options={["Automatic", "Manual"]} testId="admin-form-transmission" />
        <FormSelect label="Fuel type" value={v.fuel_type} onChange={(x) => update("fuel_type", x)} options={["Petrol", "Diesel", "Hybrid", "Electric"]} testId="admin-form-fuel" />
        <div className="sm:col-span-2">
          <FormField label="Image URL" value={v.image_url} onChange={(x) => update("image_url", x)} testId="admin-form-image" />
          <div className="mt-2 flex items-center gap-3">
            <label className={`btn-ghost !py-1.5 !px-3 !text-xs !text-[#0B1F3A] !border-black/15 cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
              <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload photo"}
              <input ref={fileRef} type="file" accept="image/*" hidden
                onChange={(e) => uploadMain(e.target.files)} data-testid="admin-form-image-upload" />
            </label>
            {v.image_url && <img src={resolveUrl(v.image_url)} alt="" className="h-12 rounded border border-black/10" />}
          </div>
        </div>
        <div className="flex gap-4 items-end pb-2">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={v.air_conditioning} onChange={(e) => update("air_conditioning", e.target.checked)} data-testid="admin-form-ac" /> A/C</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={v.available} onChange={(e) => update("available", e.target.checked)} data-testid="admin-form-available" /> Available</label>
        </div>
        <label className="sm:col-span-2 block">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#0B1F3A]/60">Description</span>
          <textarea rows="3" value={v.description} data-testid="admin-form-description"
            onChange={(e) => update("description", e.target.value)}
            className="mt-2 w-full border border-black/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" />
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <button onClick={() => onSave(v)} className="btn-gold !py-2 !px-5 !text-sm" data-testid="admin-form-save">Save</button>
      </div>
    </DialogContent>
  );
}

function FormField({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] text-[#0B1F3A]/60">{label}</span>
      <input type={type} value={value} data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full border border-black/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" />
    </label>
  );
}
function FormSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] text-[#0B1F3A]/60">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="mt-2 w-full border border-black/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#D4AF37] bg-white">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
